import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BarChart3, Plus } from "lucide-react";
import {
  useCategoryPayments,
  useMembershipStats,
  useYearlySummary,
} from "../hooks";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import {
  currentFYDates,
  toFinancialYear,
} from "../utils/financialYear";
import { MembershipAccess } from "../components/MembershipAccess";
import { MembershipNotificationsPanel } from "../components/MembershipNotificationsPanel";
import { MembershipPageHeader } from "../components/MembershipPageHeader";
import { MembershipSummaryCards } from "../components/MembershipSummaryCards";

const STATUS_COLORS: Record<string, string> = {
  PAID: "#10b981",
  NOT_PAID: "#f59e0b",
  OVERDUE: "#ef4444",
  PARTIALLY_PAID: "#3b82f6",
  WAIVED: "#6b7280",
  REFUNDED: "#8b5cf6",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(n?: number | null, currency = "KES"): string {
  if (n == null) return "—";
  return `${currency} ${n.toLocaleString("en-KE", { minimumFractionDigits: 0 })}`;
}
function pct(n?: number | null): string {
  if (n == null) return "—";
  return `${n.toFixed(1)}%`;
}

// ── Pipeline data from existing stats ────────────────────────────────────────
function usePipelineData(stats: ReturnType<typeof useMembershipStats>["data"]) {
  if (!stats) return [];
  return [
    { name: "Prospects", value: stats.prospects, fill: "#94a3b8" },
    { name: "Board Pending", value: stats.boardPending, fill: "#a855f7" },
    { name: "Board Approved", value: stats.boardApproved, fill: "#10b981" },
    { name: "Payment Pending", value: stats.paymentPending, fill: "#f97316" },
    { name: "Payment Confirmed", value: stats.paymentConfirmed, fill: "#22c55e" },
    { name: "ICT Pending", value: stats.ictPending, fill: "#38bdf8" },
    { name: "ICT Completed", value: stats.ictCompleted, fill: "#0284c7" },
    { name: "Training Pending", value: stats.trainingPending, fill: "#2dd4bf" },
    { name: "Training Completed", value: stats.trainingCompleted, fill: "#0d9488" },
    { name: "Active", value: stats.activeMembers, fill: "#15803d" },
  ].filter((d) => d.value > 0);
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; fill?: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-lg text-xs">
      <p className="font-black text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.fill ?? "#10b981" }}>
          {p.name}: <strong>{p.value.toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MembershipPortalPage() {
  const { from: defaultFrom, to: defaultTo } = currentFYDates();
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const selectedFY = fromDate ? toFinancialYear(new Date(fromDate)) : "";

  const statsQuery = useMembershipStats();
  const categoryPaymentsQuery = useCategoryPayments(selectedFY);
  const yearlySummaryQuery = useYearlySummary();

  const pipelineData = usePipelineData(statsQuery.data);

  // Active-member counts per category -- not financial, so unlike the payment
  // figures below this isn't gated behind Finance permission.
  const categoryBreakdown = [...(statsQuery.data?.categoryBreakdown ?? [])]
    .sort((a, b) => b.memberCount - a.memberCount);
  const totalCategoryMembers = categoryBreakdown.reduce((sum, c) => sum + c.memberCount, 0);

  const hasPaymentData =
    !categoryPaymentsQuery.isLoading &&
    categoryPaymentsQuery.data &&
    categoryPaymentsQuery.data.length > 0;

  const hasYearData =
    !yearlySummaryQuery.isLoading &&
    yearlySummaryQuery.data &&
    yearlySummaryQuery.data.length > 0;

  // Year-over-year line chart data
  const yearChartData = (yearlySummaryQuery.data ?? [])
    .slice()
    .reverse()
    .map((y) => ({
      year: String(y.membershipYear),
      Approved: y.newMembersApproved,
      Active: y.activeByYearEnd,
    }));

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <MembershipPageHeader
        title="Membership Dashboard"
        actions={
          <>
            <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.CREATE, MEMBERSHIP_PERMISSIONS.MANAGE]}>
              <Link to="/hr/membership/create" className="inline-flex items-center gap-2 rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white hover:bg-emerald-800">
                <Plus className="h-4 w-4" /> New Member
              </Link>
            </MembershipAccess>
            <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.REPORT_VIEW]}>
              <Link to="/hr/membership/reports" className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">
                <BarChart3 className="h-4 w-4" /> Reports
              </Link>
            </MembershipAccess>
          </>
        }
      />

      {/* ── Stats cards ──────────────────────────────────────────────── */}
      {statsQuery.data && <MembershipSummaryCards stats={statsQuery.data} />}

      {/* ── Date range selector ──────────────────────────────────────── */}
      <div className="mt-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm shadow-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm shadow-sm"
          />
        </div>
        {selectedFY && (
          <span className="mb-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700">
            {selectedFY}
          </span>
        )}
      </div>

      {/* ── Charts row 1: Pipeline + Category payments ────────────────── */}
      <div className="mt-4 grid gap-4 xl:grid-cols-2">

        {/* Pipeline funnel */}
        <section className="min-w-0 overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-black text-slate-900">Onboarding Pipeline</h2>
          {statsQuery.isLoading ? (
            <div className="h-64 flex items-center justify-center text-sm text-slate-400">Loading…</div>
          ) : pipelineData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-slate-400">No data available</div>
          ) : (
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={pipelineData} layout="vertical" margin={{ left: 16, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {pipelineData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Member categories summary */}
        <section className="min-w-0 overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-black text-slate-900">Member Categories</h2>
          {statsQuery.isLoading ? (
            <div className="h-64 flex items-center justify-center text-sm text-slate-400">Loading…</div>
          ) : !categoryBreakdown.length ? (
            <div className="h-64 flex items-center justify-center text-sm text-slate-400">No data available</div>
          ) : (
            <div className="mt-4">
              <div className="mb-4 flex items-baseline justify-between rounded-2xl bg-emerald-50 px-4 py-3">
                <span className="text-sm font-bold text-emerald-800">Total Members</span>
                <span className="text-2xl font-black text-emerald-800">{totalCategoryMembers}</span>
              </div>
              <div className="space-y-3">
                {categoryBreakdown.map((c) => {
                  const share = totalCategoryMembers > 0 ? (c.memberCount / totalCategoryMembers) * 100 : 0;
                  return (
                    <div key={c.categoryId}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-slate-700">{c.categoryName}</span>
                        <span className="font-black text-slate-900">{c.memberCount}</span>
                      </div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-emerald-500"
                          style={{ width: `${Math.max(share, c.memberCount > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── Payment rankings table ────────────────────────────────────── */}
      <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.FINANCE_VIEW, MEMBERSHIP_PERMISSIONS.FINANCE_UPDATE]}>
        <section className="mt-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-black text-slate-900">Category Payment Rankings — {selectedFY}</h2>
            </div>
            <Link to="/hr/membership/reports" className="rounded-2xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-50">
              Full Report →
            </Link>
          </div>

          {categoryPaymentsQuery.isLoading ? (
            <p className="text-sm text-slate-400 py-4">Loading…</p>
          ) : !hasPaymentData ? (
            <p className="text-sm text-slate-400 py-4">No data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Rank</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Members</th>
                    <th className="px-4 py-3">Invoiced</th>
                    <th className="px-4 py-3">Billed</th>
                    <th className="px-4 py-3">Paid</th>
                    <th className="px-4 py-3">Outstanding</th>
                    <th className="px-4 py-3">Rate</th>
                    <th className="px-4 py-3">Paid #</th>
                    <th className="px-4 py-3">Overdue #</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[...(categoryPaymentsQuery.data ?? [])]
                    .sort((a, b) => (b.totalPaid ?? 0) - (a.totalPaid ?? 0))
                    .map((c, i) => (
                      <tr key={c.categoryId} className={`hover:bg-slate-50 ${i === 0 ? "font-bold" : ""}`}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-black ${
                            i === 0 ? "bg-amber-400 text-white" :
                            i === 1 ? "bg-slate-300 text-slate-700" :
                            i === 2 ? "bg-orange-300 text-white" :
                            "bg-slate-100 text-slate-500"
                          }`}>{i + 1}</span>
                        </td>
                        <td className="px-4 py-3 font-black text-slate-900 whitespace-nowrap">{c.categoryName}</td>
                        <td className="px-4 py-3 text-slate-600">{c.memberCount}</td>
                        <td className="px-4 py-3 text-slate-600">{c.feeRecordCount}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmt(c.totalBilled)}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-bold text-emerald-700">{fmt(c.totalPaid)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-amber-700">{fmt(c.totalOutstanding)}</td>
                        <td className="px-4 py-3">
                          {(() => {
                            const rate = c.totalBilled > 0 ? (c.totalPaid / c.totalBilled) * 100 : 0;
                            return (
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-20 rounded-full bg-slate-200 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${rate >= 80 ? "bg-emerald-500" : rate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                                    style={{ width: `${Math.min(rate, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-bold">{pct(rate)}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center text-emerald-700 font-bold">{c.paidCount}</td>
                        <td className="px-4 py-3 text-center text-red-600 font-bold">{c.overdueCount}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </MembershipAccess>

      {/* ── Year-over-year trend ──────────────────────────────────────── */}
      <div className="mt-4 grid gap-4 xl:grid-cols-2">

        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.FINANCE_VIEW, MEMBERSHIP_PERMISSIONS.FINANCE_UPDATE]}>
          <section className="min-w-0 overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-black text-slate-900">Year-over-Year Trend</h2>
            {yearlySummaryQuery.isLoading ? (
              <div className="h-52 flex items-center justify-center text-sm text-slate-400">Loading…</div>
            ) : !hasYearData ? (
              <div className="h-52 flex flex-col items-center justify-center text-sm text-slate-400 gap-2">
                <p>No data available</p>
              </div>
            ) : (
              <div className="mt-4 h-52">
                <ResponsiveContainer width="100%" height={256}>
                  <LineChart data={yearChartData} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="Approved" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="Active" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        </MembershipAccess>

        {/* Year summary table */}
        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.FINANCE_VIEW, MEMBERSHIP_PERMISSIONS.FINANCE_UPDATE]}>
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-black text-slate-900">Year Summary</h2>
            {yearlySummaryQuery.isLoading ? (
              <p className="text-sm text-slate-400 py-4">Loading…</p>
            ) : !hasYearData ? (
              <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Year</th>
                      <th className="px-3 py-2">Approved</th>
                      <th className="px-3 py-2">Active</th>
                      <th className="px-3 py-2">Total Paid</th>
                      <th className="px-3 py-2">Outstanding</th>
                      <th className="px-3 py-2">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[...(yearlySummaryQuery.data ?? [])].reverse().map((y) => {
                      const rate = y.totalBilled > 0 ? (y.totalPaid / y.totalBilled) * 100 : 0;
                      return (
                        <tr key={y.membershipYear} className="hover:bg-slate-50">
                          <td className="px-3 py-2.5 font-black text-slate-900 whitespace-nowrap">{y.membershipYear}</td>
                          <td className="px-3 py-2.5 text-slate-600">{y.newMembersApproved}</td>
                          <td className="px-3 py-2.5 text-emerald-700 font-bold">{y.activeByYearEnd}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap font-bold text-emerald-700">{fmt(y.totalPaid)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap font-bold text-red-600">{fmt(y.totalOutstanding)}</td>
                          <td className="px-3 py-2.5">
                            <span className={`font-bold ${rate >= 80 ? "text-emerald-600" : rate >= 50 ? "text-amber-600" : "text-red-600"}`}>
                              {pct(rate)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </MembershipAccess>
      </div>

      {/* ── Notifications ─────────────────────────────────────────────── */}
      <div className="mt-4 grid gap-4 xl:grid-cols-4">
        <div className="xl:col-span-3">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <MembershipNotificationsPanel />
          </div>
        </div>
        <div className="hidden xl:block">
          <div className="sticky top-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="mb-4 text-xs font-black uppercase tracking-wide text-slate-400">Onboarding Pipeline</p>
            <ol className="space-y-2">
              {[
                { step: 1, label: "Prospect Created", color: "bg-slate-400" },
                { step: 2, label: "Application Received", color: "bg-slate-500" },
                { step: 3, label: "Under Review", color: "bg-amber-400" },
                { step: 4, label: "Submitted to Board", color: "bg-purple-500" },
                { step: 5, label: "Board Approved", color: "bg-emerald-500" },
                { step: 6, label: "Payment Pending", color: "bg-orange-500" },
                { step: 7, label: "Payment Confirmed", color: "bg-emerald-600" },
                { step: 8, label: "ICT Account Pending", color: "bg-sky-500" },
                { step: 9, label: "ICT Account Created", color: "bg-sky-600" },
                { step: 10, label: "Training Pending", color: "bg-teal-500" },
                { step: 11, label: "Training Completed", color: "bg-teal-600" },
                { step: 12, label: "Fully Onboarded", color: "bg-emerald-700" },
              ].map(({ step, label, color }) => (
                <li key={step} className="flex items-center gap-2">
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-black text-white ${color}`}>{step}</span>
                  <span className="text-xs text-slate-600">{label}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
